import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatPKR, CATEGORIES } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Pencil, Trash2, Plus, Upload, X, Loader2, FileSpreadsheet, AlertTriangle } from "lucide-react";
import { VARIANT_KEYS_BY_CATEGORY, SUB_COLLECTIONS } from "@/lib/beddingConfig";
import RichTextEditor from "@/components/RichTextEditor";

function resolveImage(url) {
  if (!url) return null;
  if (url.startsWith("http") || url.startsWith("data:")) return url;
  return `${process.env.REACT_APP_BACKEND_URL}${url}`;
}

const empty = {
  name: "",
  description: "",
  price: 0,
  sale_price: 0,
  category: "bedding",
  images: [],
  stock: 0,
  active: true,
  featured: false,
  sub_type: "",
  sub_collection: "",
  variant_prices: {
    comforter_6pc: 0, comforter_8pc: 0, comforter_single_4pc: 0,
    duvet_6pc: 0, duvet_8pc: 0, duvet_single_4pc: 0,
    fitted_sheet_single_1: 0, fitted_sheet_single_2: 0,
    fitted_sheet_king_2: 0, fitted_sheet_king_4: 0,
    bedsheet_single_1: 0, bedsheet_single_2: 0,
    bedsheet_king_2: 0, bedsheet_king_4: 0,
    curtain_sheer: 0, curtain_velvet: 0,
  },
  variant_sale_prices: {
    comforter_6pc: 0, comforter_8pc: 0, comforter_single_4pc: 0,
    duvet_6pc: 0, duvet_8pc: 0, duvet_single_4pc: 0,
    fitted_sheet_single_1: 0, fitted_sheet_single_2: 0,
    fitted_sheet_king_2: 0, fitted_sheet_king_4: 0,
    bedsheet_single_1: 0, bedsheet_single_2: 0,
    bedsheet_king_2: 0, bedsheet_king_4: 0,
    curtain_sheer: 0, curtain_velvet: 0,
  },
};

// Radix modals set `body { overflow: hidden }`, which (because html is the scroll
// container) makes the window lose its scroll offset. Restore only once the lock is
// released — observed via the style mutation, not a guessed delay.
// Last known scroll offset of the products list. Tracked continuously so that
// returning from any modal can restore it, whatever closed the modal.
let lastListScrollY = 0;

function restoreScroll(getTarget) {
  const apply = () => requestAnimationFrame(() => {
    const y = getTarget();
    if (y == null) return;
    const max = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
    window.scrollTo(0, Math.min(y, max));
  });
  if (getComputedStyle(document.body).overflow !== "hidden") {
    apply();
    return;
  }
  const obs = new MutationObserver(() => {
    if (getComputedStyle(document.body).overflow !== "hidden") {
      obs.disconnect();
      apply();
    }
  });
  // The scroll lock is a <style> tag injected in <head>, so watch the whole document.
  obs.observe(document.documentElement, {
    attributes: true,
    childList: true,
    subtree: true,
  });
}

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);

  // --- bulk selection ---
  const [selected, setSelected] = useState([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);

  // --- list position preservation ---
  const pendingDeleteRef = useRef(null);
  const restoreRef = useRef(false);
  const editScrollRef = useRef(0);
  const [lastEditedId, setLastEditedId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/products");
      setProducts(r.data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Keep the latest list scroll offset; modals wipe window.scrollY when they lock the body.
  useEffect(() => {
    const onScroll = () => { if (window.scrollY > 0) lastListScrollY = window.scrollY; };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Never keep a selection pointing at rows that are no longer listed.
  useEffect(() => {
    setSelected((prev) => {
      const ids = new Set(products.map((p) => p.id));
      const next = prev.filter((id) => ids.has(id));
      return next.length === prev.length ? prev : next;
    });
  }, [products]);

  const selectedSet = new Set(selected);
  const selectedProducts = products.filter((p) => selectedSet.has(p.id));
  const allSelected = products.length > 0 && selected.length === products.length;

  const toggleOne = (id) =>
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const toggleAll = () => setSelected(allSelected ? [] : products.map((p) => p.id));
  const clearSelection = () => setSelected([]);

  const bulkDelete = async () => {
    setDeleting(true);
    try {
      const { data } = await api.post("/admin/products/bulk-delete", { ids: selected });
      // Close the dialog first, then apply the removal — mutating the list while the
      // modal scroll-lock is active makes the browser lose the list position.
      pendingDeleteRef.current = data;
      setConfirmOpen(false);
      setConfirmText("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk delete failed");
    } finally {
      setDeleting(false);
    }
  };

  // Apply the deletion once the confirmation dialog has closed, then put the
  // admin back where they were (also covers Cancel).
  useEffect(() => {
    if (confirmOpen) return;
    const data = pendingDeleteRef.current;
    pendingDeleteRef.current = null;
    if (data) {
      const removed = new Set((data.deleted || []).map((d) => d.id));
      setProducts((prev) => prev.filter((p) => !removed.has(p.id)));
      setSelected((prev) => prev.filter((id) => !removed.has(id)));
      toast.success(`${removed.size} product${removed.size === 1 ? "" : "s"} deleted`);
      if (data.failed?.length) {
        toast.error(`${data.failed.length} could not be deleted: ${data.failed.map((f) => f.name).join(", ")}`);
      }
      if (data.not_found?.length) {
        toast.info(`${data.not_found.length} product(s) were already removed.`);
      }
      if (data.orphaned_images?.length) {
        toast.info(`${data.orphaned_images.length} image file(s) are now unused. They were kept in storage so past order receipts stay intact.`);
      }
    }
    if (lastListScrollY) {
      const y = lastListScrollY;
      restoreScroll(() => y);
    }
  }, [confirmOpen]);

  const open = (p) => {
    if (p) {
      editScrollRef.current = window.scrollY || lastListScrollY;
      restoreRef.current = true;
      setLastEditedId(p.id);
      setEditing(p);
      setForm({
        name: p.name,
        description: p.description || "",
        price: p.price,
        sale_price: p.sale_price || 0,
        category: p.category,
        images: p.images || [],
        stock: p.stock || 0,
        active: p.active !== false,
        featured: !!p.featured,
        sub_type: p.sub_type || "",
        sub_collection: p.sub_collection || "",
        variant_prices: {
          comforter_6pc: p.variant_prices?.comforter_6pc || p.variant_prices?.comforter || 0,
          comforter_8pc: p.variant_prices?.comforter_8pc || 0,
          comforter_single_4pc: p.variant_prices?.comforter_single_4pc || 0,
          duvet_6pc: p.variant_prices?.duvet_6pc || p.variant_prices?.duvet || 0,
          duvet_8pc: p.variant_prices?.duvet_8pc || 0,
          duvet_single_4pc: p.variant_prices?.duvet_single_4pc || 0,
          fitted_sheet_single_1: p.variant_prices?.fitted_sheet_single_1 || 0,
          fitted_sheet_single_2: p.variant_prices?.fitted_sheet_single_2 || 0,
          fitted_sheet_king_2: p.variant_prices?.fitted_sheet_king_2 || p.variant_prices?.fitted_sheet_2 || 0,
          fitted_sheet_king_4: p.variant_prices?.fitted_sheet_king_4 || p.variant_prices?.fitted_sheet_4 || 0,
          bedsheet_single_1: p.variant_prices?.bedsheet_single_1 || p.variant_prices?.bedsheet_1 || 0,
          bedsheet_single_2: p.variant_prices?.bedsheet_single_2 || 0,
          bedsheet_king_2: p.variant_prices?.bedsheet_king_2 || p.variant_prices?.bedsheet_2 || 0,
          bedsheet_king_4: p.variant_prices?.bedsheet_king_4 || p.variant_prices?.bedsheet_4 || 0,
          curtain_sheer: p.variant_prices?.curtain_sheer || 0,
          curtain_velvet: p.variant_prices?.curtain_velvet || 0,
        },
        variant_sale_prices: {
          comforter_6pc: p.variant_sale_prices?.comforter_6pc || 0,
          comforter_8pc: p.variant_sale_prices?.comforter_8pc || 0,
          comforter_single_4pc: p.variant_sale_prices?.comforter_single_4pc || 0,
          duvet_6pc: p.variant_sale_prices?.duvet_6pc || 0,
          duvet_8pc: p.variant_sale_prices?.duvet_8pc || 0,
          duvet_single_4pc: p.variant_sale_prices?.duvet_single_4pc || 0,
          fitted_sheet_single_1: p.variant_sale_prices?.fitted_sheet_single_1 || 0,
          fitted_sheet_single_2: p.variant_sale_prices?.fitted_sheet_single_2 || 0,
          fitted_sheet_king_2: p.variant_sale_prices?.fitted_sheet_king_2 || 0,
          fitted_sheet_king_4: p.variant_sale_prices?.fitted_sheet_king_4 || 0,
          bedsheet_single_1: p.variant_sale_prices?.bedsheet_single_1 || 0,
          bedsheet_single_2: p.variant_sale_prices?.bedsheet_single_2 || 0,
          bedsheet_king_2: p.variant_sale_prices?.bedsheet_king_2 || 0,
          bedsheet_king_4: p.variant_sale_prices?.bedsheet_king_4 || 0,
          curtain_sheer: p.variant_sale_prices?.curtain_sheer || 0,
          curtain_velvet: p.variant_sale_prices?.curtain_velvet || 0,
        },
      });
    } else {
      setEditing(null);
      setForm(empty);
    }
  };
  const close = () => { setEditing(null); setForm(empty); };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    try {
      for (const file of files) {
        if (!file.type.startsWith("image/")) {
          toast.error(`${file.name} is not an image`);
          continue;
        }
        if (file.size > 6 * 1024 * 1024) {
          toast.error(`${file.name} exceeds 6 MB`);
          continue;
        }
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/admin/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setForm((f) => ({ ...f, images: [...f.images, data.url] }));
      }
      toast.success(`${files.length} image${files.length > 1 ? "s" : ""} uploaded`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const save = async () => {
    if (saving) return;
    if (!form.name || form.price <= 0) {
      toast.error("Name and base price are required");
      return;
    }
    const payload = { ...form };
    // Coerce sale_price (>0, <price)
    const sp = Number(form.sale_price || 0);
    payload.sale_price = sp > 0 && sp < Number(form.price) ? sp : null;
    const categoryVariants = VARIANT_KEYS_BY_CATEGORY[form.category];
    if (categoryVariants) {
      const cleaned = {};
      const cleanedSale = {};
      for (const v of categoryVariants) {
        const base = Number(form.variant_prices?.[v.key] || 0);
        if (base > 0) cleaned[v.key] = base;
        const sale = Number(form.variant_sale_prices?.[v.key] || 0);
        if (sale > 0 && sale < base) cleanedSale[v.key] = sale;
      }
      payload.variant_prices = Object.keys(cleaned).length ? cleaned : null;
      payload.variant_sale_prices = Object.keys(cleanedSale).length ? cleanedSale : null;
    } else {
      payload.variant_prices = null;
      payload.variant_sale_prices = null;
    }
    try {
      setSaving(true);
      if (editing && editing !== "new") {
        const { data } = await api.put(`/admin/products/${editing.id}`, payload);
        // Patch just this row instead of refetching, so the list never remounts or jumps.
        setProducts((prev) => prev.map((p) => (p.id === editing.id ? { ...p, ...data } : p)));
        toast.success("Product updated");
        close();
      } else {
        const { data } = await api.post("/admin/products", payload);
        setProducts((prev) => [data, ...prev]);
        toast.success("Product created");
        close();
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await api.delete(`/admin/products/${id}`);
    setProducts((prev) => prev.filter((p) => p.id !== id));
    toast.success("Product deleted");
  };

  const open_modal = !!editing || editing === "new";

  // Put the admin back where they were once the editor closes (save or cancel).
  useEffect(() => {
    if (open_modal || !restoreRef.current) return;
    restoreRef.current = false;
    const y = editScrollRef.current;
    const id = lastEditedId;
    restoreScroll(() => {
      // The edited row is patched in place, so the saved offset still shows it.
      // Only recentre if the row somehow left the saved viewport.
      const el = id ? document.querySelector(`[data-testid="product-row-${id}"]`) : null;
      if (!el) return y;
      const rowTop = el.getBoundingClientRect().top + window.scrollY;
      const visibleAtSaved = rowTop >= y - 8 && rowTop <= y + window.innerHeight - 60;
      return visibleAtSaved ? y : Math.max(0, rowTop - window.innerHeight / 2);
    });
  }, [open_modal, lastEditedId]);

  return (
    <div className="p-6 lg:p-10 max-w-7xl mx-auto" data-testid="admin-products-page">
      <div className="flex justify-between items-end mb-8">
        <div>
          <div className="label-eyebrow mb-1">Catalog</div>
          <h1 className="font-display text-4xl text-ink">Products</h1>
        </div>
        <div className="flex gap-2">
          <Link to="/admin/import">
            <Button
              variant="outline"
              className="uppercase tracking-widest text-xs"
              data-testid="bulk-import-btn"
            >
              <FileSpreadsheet size={14} className="mr-1" /> Bulk Import
            </Button>
          </Link>
          <Button
            onClick={() => setEditing("new")}
            className="bg-sage hover:bg-sage-dark text-cream uppercase tracking-widest text-xs"
            data-testid="add-product-btn"
          >
            <Plus size={14} className="mr-1" /> Add Product
          </Button>
        </div>
      </div>

      {/* Selection toolbar — only appears when rows are selected */}
      {selected.length > 0 && (
        <div
          className="sticky top-0 z-20 mb-3 flex flex-wrap items-center justify-between gap-3 bg-ink text-cream px-4 py-3"
          data-testid="selection-toolbar"
        >
          <div className="flex items-center gap-4">
            <span className="text-sm font-semibold" data-testid="selection-count">
              {selected.length} Product{selected.length === 1 ? "" : "s"} Selected
            </span>
            <button
              onClick={clearSelection}
              className="text-[11px] uppercase tracking-widest text-cream/70 hover:text-cream underline"
              data-testid="clear-selection-btn"
            >
              Clear Selection
            </button>
          </div>
          <Button
            onClick={() => { setConfirmText(""); setConfirmOpen(true); }}
            className="bg-red-600 hover:bg-red-700 text-cream uppercase tracking-widest text-xs"
            data-testid="delete-selected-btn"
          >
            <Trash2 size={14} className="mr-1.5" /> Delete Selected ({selected.length})
          </Button>
        </div>
      )}

      <div className="bg-white border border-border-subtle overflow-x-auto">
        <table className="w-full min-w-[720px]">
          <thead className="bg-cream-warm border-b border-border-subtle">
            <tr className="text-left text-[10px] uppercase tracking-widest text-ink-soft">
              <th className="p-4 w-10">
                <Checkbox
                  checked={allSelected}
                  onCheckedChange={toggleAll}
                  aria-label="Select all listed products"
                  data-testid="select-all-checkbox"
                />
              </th>
              <th className="p-4">Product</th>
              <th className="p-4">Category</th>
              <th className="p-4">Price</th>
              <th className="p-4">Stock</th>
              <th className="p-4">Status</th>
              <th className="p-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="p-6 text-ink-soft text-center">Loading…</td></tr>
            ) : products.length === 0 ? (
              <tr><td colSpan={7} className="p-10 text-center">
                <div className="font-display text-2xl text-ink mb-1">No products yet</div>
                <div className="text-ink-soft text-sm">Click "Add Product" to start your catalog.</div>
              </td></tr>
            ) : products.map((p) => {
              const isSelected = selectedSet.has(p.id);
              return (
              <tr
                key={p.id}
                className={`border-b border-border-subtle last:border-0 transition-colors ${
                  isSelected ? "bg-sage/10" : "hover:bg-cream-warm/50"
                } ${lastEditedId === p.id ? "ring-1 ring-inset ring-sage/60" : ""}`}
                data-testid={`product-row-${p.id}`}
              >
                <td className="p-4">
                  <Checkbox
                    checked={isSelected}
                    onCheckedChange={() => toggleOne(p.id)}
                    aria-label={`Select ${p.name}`}
                    data-testid={`select-product-${p.id}`}
                  />
                </td>
                <td className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-cream-warm flex-shrink-0 overflow-hidden">
                      {p.images?.[0] && <img src={resolveImage(p.images[0])} alt="" className="w-full h-full object-cover" />}
                    </div>
                    <div>
                      <div className="font-semibold text-ink">{p.name}</div>
                      {p.sku && <div className="text-[11px] font-mono text-ink-soft">{p.sku}</div>}
                      {p.sub_type && <div className="text-xs text-ink-soft">{p.sub_type}</div>}
                    </div>
                  </div>
                </td>
                <td className="p-4 text-ink-soft text-sm">
                  {CATEGORIES.find((c) => c.slug === p.category)?.label}
                </td>
                <td className="p-4 text-ink">{formatPKR(p.price)}</td>
                <td className="p-4 text-ink-soft">{p.stock}</td>
                <td className="p-4">
                  <span className={`px-2 py-1 text-[10px] uppercase tracking-widest ${p.featured ? "bg-rose/15 text-rose-dark" : "bg-ink/5 text-ink-soft"}`}>
                    {p.featured ? "Featured" : "In Catalog"}
                  </span>
                </td>
                <td className="p-4 text-right">
                  <button onClick={() => open(p)} className="p-2 text-ink-soft hover:text-sage" data-testid={`edit-product-${p.id}`}>
                    <Pencil size={14} />
                  </button>
                  <button onClick={() => remove(p.id)} className="p-2 text-ink-soft hover:text-rose-dark" data-testid={`delete-product-${p.id}`}>
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Dialog open={open_modal} onOpenChange={(v) => !v && close()}>
        <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="product-form-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-3xl text-ink">
              {editing && editing !== "new" ? "Edit Product" : "Add Product"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-ink-soft text-xs uppercase tracking-widest">Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" data-testid="product-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-ink-soft text-xs uppercase tracking-widest">
                  {VARIANT_KEYS_BY_CATEGORY[form.category] ? "Base Price (PKR)" : "Price (PKR)"}
                </Label>
                <Input type="number" value={form.price} onChange={(e) => setForm({ ...form, price: Number(e.target.value) })} className="mt-1.5" data-testid="product-price-input" />
                {VARIANT_KEYS_BY_CATEGORY[form.category] && (
                  <p className="text-[11px] text-ink-soft mt-1">Used as fallback if a variant price is empty.</p>
                )}
              </div>
              <div>
                <Label className="text-ink-soft text-xs uppercase tracking-widest">Stock</Label>
                <Input type="number" value={form.stock} onChange={(e) => setForm({ ...form, stock: Number(e.target.value) })} className="mt-1.5" />
              </div>
            </div>

            {/* Product-level sale price — used for non-variant categories OR as a fallback */}
            {!VARIANT_KEYS_BY_CATEGORY[form.category] && (
              <div>
                <Label className="text-ink-soft text-xs uppercase tracking-widest">Sale Price (PKR) — optional</Label>
                <Input
                  type="number"
                  min={0}
                  value={form.sale_price || 0}
                  onChange={(e) => setForm({ ...form, sale_price: Number(e.target.value) })}
                  className="mt-1.5"
                  data-testid="product-sale-price-input"
                  placeholder="Leave 0 for no sale"
                />
                <p className="text-[11px] text-ink-soft mt-1">If set and lower than the Base Price, the product card and PDP will show the base struck through plus a discount % badge.</p>
              </div>
            )}
            <div>
              <Label className="text-ink-soft text-xs uppercase tracking-widest">Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v, sub_collection: "" })}>
                <SelectTrigger className="mt-1.5" data-testid="product-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => (
                    <SelectItem key={c.slug} value={c.slug} data-testid={`category-option-${c.slug}`}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Sub-collection (bedding only) */}
            {SUB_COLLECTIONS[form.category] && (
              <div data-testid="sub-collection-section">
                <Label className="text-ink-soft text-xs uppercase tracking-widest">Sub-Collection</Label>
                <Select value={form.sub_collection || ""} onValueChange={(v) => setForm({ ...form, sub_collection: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="product-sub-collection-select">
                    <SelectValue placeholder="Choose sub-collection" />
                  </SelectTrigger>
                  <SelectContent>
                    {SUB_COLLECTIONS[form.category].map((s) => (
                      <SelectItem key={s.slug} value={s.slug} data-testid={`sub-collection-option-${s.slug}`}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-ink-soft mt-1">Customers pick a sub-collection before browsing bedding products.</p>
              </div>
            )}

            {/* Per-variant prices for bedding & curtains */}
            {VARIANT_KEYS_BY_CATEGORY[form.category] && (
              <div className="bg-cream-warm border border-border-subtle p-4" data-testid="variant-prices-section">
                <div className="label-eyebrow !text-[10px] mb-1">Variant Prices</div>
                <p className="text-xs text-ink-soft mb-4">
                  Set a <span className="font-semibold text-ink">Base</span> price per variant (leave 0 to fall back to the product Base Price).
                  Optionally set a <span className="font-semibold text-rose-dark">Sale</span> price — when it's lower than the base, the product page shows the base struck through plus a discount % badge.
                </p>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-6 gap-y-3">
                  {VARIANT_KEYS_BY_CATEGORY[form.category].map((v) => {
                    const base = Number(form.variant_prices?.[v.key] || 0);
                    const sale = Number(form.variant_sale_prices?.[v.key] || 0);
                    const validSale = sale > 0 && sale < base;
                    const pct = validSale ? Math.round(((base - sale) / base) * 100) : 0;
                    return (
                      <div key={v.key} className="border border-border-subtle bg-cream p-3">
                        <div className="flex items-center justify-between mb-2 gap-2">
                          <Label className="text-ink text-[11px] uppercase tracking-widest font-semibold leading-tight">{v.label}</Label>
                          {validSale && (
                            <span className="bg-rose-dark text-cream text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5" data-testid={`variant-pct-${v.key}`}>
                              −{pct}%
                            </span>
                          )}
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <div className="text-[9px] uppercase tracking-widest text-ink-soft mb-1">Base</div>
                            <Input
                              type="number"
                              min={0}
                              value={form.variant_prices?.[v.key] ?? 0}
                              onChange={(e) =>
                                setForm((f) => ({
                                  ...f,
                                  variant_prices: { ...(f.variant_prices || {}), [v.key]: Number(e.target.value) },
                                }))
                              }
                              placeholder={v.placeholder}
                              className="bg-white text-sm h-9"
                              data-testid={`variant-price-${v.key}`}
                            />
                          </div>
                          <div>
                            <div className="text-[9px] uppercase tracking-widest text-rose-dark mb-1">Sale</div>
                            <Input
                              type="number"
                              min={0}
                              value={form.variant_sale_prices?.[v.key] ?? 0}
                              onChange={(e) =>
                                setForm((f) => ({
                                  ...f,
                                  variant_sale_prices: { ...(f.variant_sale_prices || {}), [v.key]: Number(e.target.value) },
                                }))
                              }
                              placeholder="0 = no sale"
                              className="bg-white text-sm h-9"
                              data-testid={`variant-sale-price-${v.key}`}
                            />
                          </div>
                        </div>
                        {sale > 0 && !validSale && (
                          <p className="text-[10px] text-red-600 mt-1.5" data-testid={`variant-sale-warning-${v.key}`}>
                            Sale must be lower than base to apply.
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            <div>
              <Label className="text-ink-soft text-xs uppercase tracking-widest">Sub-type / Tag (optional)</Label>
              <Input value={form.sub_type} onChange={(e) => setForm({ ...form, sub_type: e.target.value })} placeholder="e.g. Curtain, Bath Towel" className="mt-1.5" />
            </div>
            <div>
              <Label className="text-ink-soft text-xs uppercase tracking-widest">Description</Label>
              <div className="mt-1.5">
                <RichTextEditor
                  value={form.description}
                  onChange={(html) => setForm({ ...form, description: html })}
                  placeholder="Write the product description… use the toolbar to add headings, bold, lists. Example: H2 'Product Includes' followed by a bulleted list."
                  testId="product-description-editor"
                />
              </div>
              <p className="text-[11px] text-ink-soft mt-1.5 leading-relaxed">
                Tip: Use Heading 2 for section titles (e.g. <em>Product Includes</em>, <em>Dimensions</em>, <em>Material</em>) and bullet lists for items. Plain-text descriptions from older products are still auto-formatted on the product page.
              </p>
            </div>
            <div>
              <Label className="text-ink-soft text-xs uppercase tracking-widest">Images</Label>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp,image/gif"
                multiple
                onChange={handleFileUpload}
                className="hidden"
                data-testid="product-image-file-input"
              />
              <div className="mt-1.5">
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="w-full flex flex-col items-center justify-center gap-2 border-2 border-dashed border-border-subtle hover:border-sage hover:bg-cream-warm/40 transition-colors py-8 text-ink-soft hover:text-sage disabled:opacity-50"
                  data-testid="upload-image-btn"
                >
                  {uploading ? (
                    <><Loader2 size={20} className="animate-spin" /><span className="text-sm">Uploading…</span></>
                  ) : (
                    <><Upload size={20} /><span className="text-sm font-semibold uppercase tracking-widest">Click to upload images</span><span className="text-xs">JPG, PNG, WEBP — up to 6 MB each. Multiple files allowed.</span></>
                  )}
                </button>
              </div>
              {form.images.length > 0 && (
                <div className="mt-3 grid grid-cols-4 gap-2" data-testid="product-images-preview">
                  {form.images.map((img, i) => (
                    <div key={i} className="relative aspect-square bg-cream-warm border border-border-subtle group">
                      <img src={resolveImage(img)} alt="" className="w-full h-full object-cover" />
                      {i === 0 && (
                        <span className="absolute top-1 left-1 bg-sage text-cream text-[9px] uppercase tracking-widest px-1.5 py-0.5">Cover</span>
                      )}
                      <button
                        type="button"
                        onClick={() => setForm((f) => ({ ...f, images: f.images.filter((_, idx) => idx !== i) }))}
                        className="absolute top-1 right-1 bg-ink/80 text-cream p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                        data-testid={`remove-image-${i}`}
                      >
                        <X size={10} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="flex items-center justify-between pt-2 border-t border-border-subtle">
              <div className="flex items-center gap-2">
                <Switch checked={form.featured} onCheckedChange={(v) => setForm({ ...form, featured: v })} data-testid="product-featured-toggle" />
                <div>
                  <div className="text-sm text-ink">Show in New Arrivals</div>
                  <div className="text-[11px] text-ink-soft">Featured on the homepage. Products always appear in their category regardless.</div>
                </div>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={close} disabled={saving}>Cancel</Button>
                <Button onClick={save} disabled={saving} className="bg-sage hover:bg-sage-dark text-cream disabled:opacity-60" data-testid="save-product-btn">
                  {saving ? <><Loader2 size={14} className="mr-1.5 animate-spin" /> Saving…</> : "Save"}
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk delete confirmation */}
      <AlertDialog open={confirmOpen} onOpenChange={(v) => { if (!v) { setConfirmOpen(false); setConfirmText(""); } }}>
        <AlertDialogContent
          className="bg-white max-w-lg"
          onCloseAutoFocus={(e) => e.preventDefault()}
          data-testid="bulk-delete-dialog"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-2xl text-ink flex items-center gap-2">
              <AlertTriangle size={20} className="text-red-600" />
              Delete {selectedProducts.length} product{selectedProducts.length === 1 ? "" : "s"}?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-ink-soft">
              You are about to permanently delete {selectedProducts.length} product
              {selectedProducts.length === 1 ? "" : "s"} from Bedding Lounge. This action cannot be undone
              through the admin panel. Only the products listed below are affected — past orders,
              customers and all other products stay exactly as they are.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="max-h-52 overflow-y-auto border border-border-subtle divide-y divide-border-subtle" data-testid="bulk-delete-list">
            {selectedProducts.map((p) => (
              <div key={p.id} className="flex items-center gap-3 px-3 py-2">
                <div className="w-8 h-8 bg-cream-warm shrink-0 overflow-hidden">
                  {p.images?.[0] && <img src={resolveImage(p.images[0])} alt="" className="w-full h-full object-cover" />}
                </div>
                <div className="min-w-0">
                  <div className="text-sm text-ink truncate">{p.name}</div>
                  <div className="text-[11px] font-mono text-ink-soft">{p.sku || "no SKU"}</div>
                </div>
              </div>
            ))}
          </div>

          <div>
            <Label className="text-ink-soft text-xs uppercase tracking-widest">
              Type DELETE to confirm
            </Label>
            <Input
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="DELETE"
              className="mt-1.5"
              data-testid="bulk-delete-confirm-input"
            />
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel data-testid="bulk-delete-cancel-btn">Cancel</AlertDialogCancel>
            <Button
              onClick={bulkDelete}
              disabled={confirmText.trim() !== "DELETE" || deleting || selectedProducts.length === 0}
              className="bg-red-600 hover:bg-red-700 text-cream disabled:opacity-50"
              data-testid="bulk-delete-confirm-btn"
            >
              {deleting ? <><Loader2 size={14} className="mr-1.5 animate-spin" /> Deleting…</> : `Confirm Delete (${selectedProducts.length})`}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
